// useState whose value is mirrored to localStorage under `key`, so a choice (e.g.
// the Clients density filter) survives reloads. Falls back to `initial` when there
// is no stored value or storage is unavailable (SSR / tests / private mode).

import { useCallback, useState } from 'react';

export function usePersistentState<T extends string>(
  key: string,
  initial: T,
): [T, (v: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
      return (raw as T) ?? initial;
    } catch {
      return initial;
    }
  });

  const set = useCallback(
    (v: T) => {
      setValue(v);
      try {
        localStorage?.setItem(key, v);
      } catch {
        /* ignore quota / unavailable storage */
      }
    },
    [key],
  );

  return [value, set];
}
